/**
 * IOSInstallPrompt — a one-time bottom-sheet that nudges iPhone/iPad Safari
 * visitors to add the site to their Home Screen. iOS Safari does not honor
 * the standard beforeinstallprompt event, so we detect iOS Safari and show
 * our own native-looking sheet with the exact Share → Add to Home Screen
 * steps.
 *
 * Dismissal is persisted to localStorage so we don't nag.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share, Plus, X } from "lucide-react";

const KEY = "holoclub_ios_a2hs_dismissed_at";
const RENAG_DAYS = 14;

function detectIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
  // Standalone = already installed
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  // Safari (not Chrome/Firefox/Edge on iOS — those don't support A2HS reliably)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari && !isStandalone;
}

export default function IOSInstallPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!detectIOSSafari()) return;
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const ago = (Date.now() - Number(raw)) / (1000 * 60 * 60 * 24);
      if (ago < RENAG_DAYS) return;
    }
    // Show after a short delay so the user sees the hero first
    const t = setTimeout(() => setShow(true), 6500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="a2hs"
          initial={{ y: 200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 200, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 220 }}
          className="fixed left-0 right-0 bottom-4 px-4 z-[120] safe-bottom"
          data-testid="ios-install-prompt"
        >
          <div className="mx-auto max-w-md border border-[#00E5FF]/40 bg-[#0B0608]/95 backdrop-blur-xl shadow-[0_20px_60px_-10px_rgba(0,229,255,0.4)] p-4">
            <div className="flex items-start gap-3">
              <img src="/apple-touch-icon.png" alt="" className="w-12 h-12 shrink-0 rounded-[12px] border border-white/10" />
              <div className="flex-1 min-w-0">
                <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">Add to iPhone</div>
                <h3 className="mt-1 font-display font-black tracking-tighter text-lg leading-tight">Hologram Club. On your home screen.</h3>
                <p className="mt-1.5 text-white/65 text-[13px] leading-snug">
                  Tap <Share size={13} className="inline -mt-0.5 text-[#00E5FF]" /> in Safari, then <span className="text-white">&ldquo;Add to Home Screen&rdquo;</span> <Plus size={13} className="inline -mt-0.5 text-[#00E5FF]" />.
                </p>
              </div>
              <button
                onClick={dismiss}
                className="w-8 h-8 -mr-1 -mt-1 rounded-full border border-white/15 bg-black/40 flex items-center justify-center text-white/65 hover:text-white"
                aria-label="Dismiss"
                data-testid="ios-install-prompt-close"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
